import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { Model } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

type ModelCreate = Omit<Model, 'id' | 'created_at' | 'updated_at'>;

interface ModelMonthlyEarning {
  model_id: string;
  month: string; // ISO date string (YYYY-MM-01)
  earnings: number;
  created_at: string;
  updated_at: string;
}

const SupabaseModelService = {
  async getModels(): Promise<Model[]> {
    const { data, error } = await supabase
      .from('models')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching models:', error);
      throw error;
    }

    return data.map(model => ({
      id: model.id,
      name: model.name,
      set: model.set,
      pay_class: model.pay_class,
      account_type: model.account_type,
      groups: model.groups,
      spam_post_group: model.spam_post_group,
      fan_gain_group: model.fan_gain_group,
      weekend_group: model.weekend_group,
      scheduling_group: model.scheduling_group,
      scheduling_sub_groups: model.scheduling_sub_groups,
      paused: model.paused,
      created_at: DateTime.fromISO(model.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(model.updated_at, { zone: 'UTC' }),
    })) as Model[];
  },

  async getModel(id: string): Promise<Model | null> {
    const { data, error } = await supabase
      .from('models')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) return null;

    return {
      id: data.id,
      name: data.name,
      set: data.set,
      pay_class: data.pay_class,
      account_type: data.account_type,
      groups: data.groups,
      spam_post_group: data.spam_post_group,
      fan_gain_group: data.fan_gain_group,
      weekend_group: data.weekend_group,
      scheduling_group: data.scheduling_group,
      scheduling_sub_groups: data.scheduling_sub_groups,
      paused: data.paused,
      created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' }),
    } as Model;
  },

  async createModel(model: ModelCreate): Promise<string> {
    const { data, error } = await supabase
      .from('models')
      .insert({
        name: model.name,
        set: model.set,
        pay_class: model.pay_class,
        account_type: model.account_type,
        groups: model.groups || ['Shifts'],
        spam_post_group: model.spam_post_group,
        fan_gain_group: model.fan_gain_group,
        weekend_group: model.weekend_group,
        scheduling_group: model.scheduling_group,
        scheduling_sub_groups: model.scheduling_sub_groups,
        paused: model.paused,
      })
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async updateModel(id: string, model: Partial<Model>): Promise<void> {
    const updated_data: any = {};
    
    if (model.name !== undefined) updated_data.name = model.name;
    if (model.set !== undefined) updated_data.set = model.set;
    if (model.pay_class !== undefined) updated_data.pay_class = model.pay_class;
    if (model.account_type !== undefined) updated_data.account_type = model.account_type;
    if (model.groups !== undefined) updated_data.groups = model.groups;
    if (model.spam_post_group !== undefined) updated_data.spam_post_group = model.spam_post_group;
    if (model.fan_gain_group !== undefined) updated_data.fan_gain_group = model.fan_gain_group;
    if (model.weekend_group !== undefined) updated_data.weekend_group = model.weekend_group;
    if (model.scheduling_group !== undefined) updated_data.scheduling_group = model.scheduling_group;
    if (model.scheduling_sub_groups !== undefined) updated_data.scheduling_sub_groups = model.scheduling_sub_groups;
    if (model.paused !== undefined) updated_data.paused = model.paused;

    const { data, error } = await supabase
      .from('models')
      .update(updated_data)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Supabase update error:', error);
      throw error;
    }

    if (data.length === 0) {
      throw new Error('No rows were updated - this may be a permissions issue');
    }
  },

  async deleteModel(id: string): Promise<void> {
    const { error } = await supabase
      .from('models')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getModelEarningsForMonth(model_id: string, month: string): Promise<ModelMonthlyEarning | null> {
    const { data, error } = await supabase
      .from('model_monthly_earnings')
      .select('*')
      .eq('model_id', model_id)
      .eq('month', month)
      .single();

    if (error || !data) return null;
    return data as ModelMonthlyEarning;
  },

  async getModelEarnings(model_id: string): Promise<ModelMonthlyEarning[]> {
    const { data, error } = await supabase
      .from('model_monthly_earnings')
      .select('*')
      .eq('model_id', model_id)
      .order('month', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getModelEarningsForYear(model_id: string, year: number): Promise<ModelMonthlyEarning[]> {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-01`;
    
    const { data, error } = await supabase
      .from('model_monthly_earnings')
      .select('*')
      .eq('model_id', model_id)
      .gte('month', startDate)
      .lte('month', endDate)
      .order('month', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getAllModelsEarningsForYear(year: number): Promise<ModelMonthlyEarning[]> {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-01`;
    
    const { data, error } = await supabase
      .from('model_monthly_earnings')
      .select('*')
      .gte('month', startDate)
      .lte('month', endDate)
      .order('model_id', { ascending: true })
      .order('month', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async toggleModelPause(model_id: string, paused: boolean): Promise<void> {
    const { error } = await supabase
      .from('models')
      .update({ paused })
      .eq('id', model_id);

    if (error) throw error;
  },

  async recalculateAllModelEarnings(): Promise<void> {
    const { data, error } = await supabase.functions.invoke('calculate-model-earnings', {
      body: { allModels: true }
    });
    if (error) throw error;
    return data;
  },

  async recalculateAllModelEarningsForYear(): Promise<void> {
    const currentDate = DateTime.utc();
    const currentYear = currentDate.year;
    const currentMonth = currentDate.month;
    
    const promises = [];
    
    for (let month = 1; month <= currentMonth; month++) {
      promises.push(
        supabase.functions.invoke('calculate-model-earnings', {
          body: { allModels: true, year: currentYear, month }
        })
      );
    }
    
    const results = await Promise.allSettled(promises);
    
    const errors = results
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map(result => result.reason);
    
    if (errors.length > 0) {
      console.error('Some month calculations failed:', errors);
      throw new Error(`${errors.length} month calculations failed`);
    }
  },

  async calculateModelEarningsForMonth(model_id: string, year: number, month: number): Promise<number> {
    const { data, error } = await supabase.functions.invoke('calculate-model-earnings', {
      body: { model_id, year, month }
    });
    if (error) throw error;
    return data.earnings;
  },
}

export const useModels = ({enabled = true}: {enabled?: boolean} = {}) => {
  return useQuery({
    queryKey: ['supabase-models'],
    queryFn: () => SupabaseModelService.getModels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled,
  });
};

export const useModel = (id: string) => {
  return useQuery({
    queryKey: ['supabase-models', id],
    queryFn: () => SupabaseModelService.getModel(id),
    enabled: !!id,
  });
};

export const useModelEarningsForMonth = (model_id: string, month: string) => {
  return useQuery({
    queryKey: ['supabase-models', model_id, 'earnings', month],
    queryFn: () => SupabaseModelService.getModelEarningsForMonth(model_id, month),
    enabled: !!model_id && !!month,
  });
};

export const useModelEarnings = (model_id: string) => {
  return useQuery({
    queryKey: ['supabase-models', model_id, 'earnings'],
    queryFn: () => SupabaseModelService.getModelEarnings(model_id),
    enabled: !!model_id,
  });
};

export const useModelEarningsForYear = (model_id: string, year: number) => {
  return useQuery({
    queryKey: ['supabase-models', model_id, 'earnings', year],
    queryFn: () => SupabaseModelService.getModelEarningsForYear(model_id, year),
    enabled: !!model_id && !!year,
  });
};

export const useAllModelsEarningsForYear = (year: number) => {
  return useQuery({
    queryKey: ['supabase-models', 'earnings', year],
    queryFn: () => SupabaseModelService.getAllModelsEarningsForYear(year),
    enabled: !!year,
  });
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: SupabaseModelService.createModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
    },
  });
};

export const useUpdateModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, model }: { id: string; model: Partial<Model> }) => 
      SupabaseModelService.updateModel(id, model),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', id] });
    },
    onMutate: async ({ id, model }) => {
      await queryClient.cancelQueries({ queryKey: ['supabase-models', id] });
      const previousModel = queryClient.getQueryData(['supabase-models', id]);
      
      queryClient.setQueryData(['supabase-models', id], (old: any) => ({
        ...old,
        ...model,
      }));
      
      return { previousModel };
    },
    onError: (__, _, context: any) => {
      if (context?.previousModel) {
        queryClient.setQueryData(['supabase-models', context.previousModel.id], context.previousModel);
      }
    },
  });
};

export const useDeleteModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => SupabaseModelService.deleteModel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
    },
  });
};

export const useToggleModelPause = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ model_id, paused }: { model_id: string; paused: boolean }) => {
      try {
        return SupabaseModelService.toggleModelPause(model_id, paused);
      } catch (error: any) {
        throw new Error(error.message || 'Failed to toggle model pause status');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
    },
    onError: (error: any) => {
      throw new Error(error.message || 'Failed to toggle model pause status');
    },
  });
};

export const useRecalculateAllModelEarnings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: SupabaseModelService.recalculateAllModelEarnings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', 'earnings'] });
    },
  });
};

export const useRecalculateAllModelEarningsForYear = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: SupabaseModelService.recalculateAllModelEarningsForYear,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', 'earnings'] });
    },
  });
};

export const useCalculateModelEarningsForMonth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ model_id, year, month }: { model_id: string; year: number; month: number }) => 
      SupabaseModelService.calculateModelEarningsForMonth(model_id, year, month),
    onSuccess: (_, { model_id, year, month }) => {
      queryClient.invalidateQueries({ queryKey: ['supabase-models', model_id, 'earnings'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', model_id, 'earnings', year] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', model_id, 'earnings', `${year}-${String(month).padStart(2, '0')}-01`] });
      queryClient.invalidateQueries({ queryKey: ['supabase-models', 'earnings', year] });
    },
  });
};

export default SupabaseModelService;